import {
  ListPage,
  RefreshButton,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@skylabs-digital/backoffice-kit';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStorageActions } from '../api/storage.api';
import { AppFilter } from '../components/shared/AppFilter';
import { StorageByAppTable } from '../components/storage/StorageByAppTable';
import { StorageChart } from '../components/storage/StorageChart';
import { StorageKpis } from '../components/storage/StorageKpis';
import {
  type StorageOverview,
  type StorageSeries,
  type StorageWindow,
  storageWindowSchema,
} from '../schemas/storage.schema';

const WINDOWS: StorageWindow[] = ['7d', '30d', '90d'];

export function StoragePage() {
  const api = useStorageActions();
  const [params, setParams] = useSearchParams();
  const appId = params.get('app') ?? undefined;
  const parsed = storageWindowSchema.safeParse(params.get('window'));
  const window: StorageWindow = parsed.success ? parsed.data : '30d';
  const [overview, setOverview] = useState<StorageOverview | null>(null);
  const [series, setSeries] = useState<StorageSeries | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const setParam = (key: 'app' | 'window', value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [o, s] = await Promise.all([api.overview(), api.series({ window, appId })]);
      setOverview(o);
      setSeries(s);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to load storage stats'));
    } finally {
      setRefreshing(false);
    }
  }, [api, window, appId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = overview
    ? appId
      ? overview.apps.filter((a) => a.appId === appId)
      : overview.apps
    : [];

  return (
    <ListPage
      title="Storage"
      description="How much each app stores, how close it is to its quota and how fast it grows."
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <AppFilter value={appId} onChange={(v) => setParam('app', v)} />
          <Tabs value={window} onValueChange={(v) => setParam('window', v)}>
            <TabsList>
              {WINDOWS.map((w) => (
                <TabsTrigger key={w} value={w}>
                  {w}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <RefreshButton onRefresh={load} isRefreshing={refreshing} label={null} />
        </div>
      }
    >
      <div className="space-y-6">
        {error ? <p className="text-sm text-destructive">{error.message}</p> : null}
        <StorageKpis overview={overview} />
        <StorageChart series={series} />
        <section className="space-y-2">
          <h2 className="text-base font-semibold">By app</h2>
          <StorageByAppTable
            rows={rows}
            loading={overview === null}
            onSelectApp={(id) => setParam('app', id)}
          />
        </section>
      </div>
    </ListPage>
  );
}
